import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Film, HardDrive, Activity, Sparkles, Copy, Upload, Heart, FolderPlus
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import { api, fmtBytes, fmtDuration } from "@/lib/api";

const QUALITY_COLORS = { KEEP: "#10B981", GOOD: "#00F0FF", REVIEW: "#F59E0B", POOR: "#fb923c", REJECTED: "#EF4444", UNRATED: "#64748B" };

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="studio-card p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon size={16} style={{ color: accent }} />
        </div>
        <span className="metric-label">{label}</span>
      </div>
      <div className="font-head text-3xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-[#64748B] text-xs mt-1 font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { project, projectId } = useApp();
  const navigate = useNavigate();
  const { data } = usePoll(() => (projectId ? api.dashboard(projectId) : Promise.resolve(null)), 2500, [projectId]);
  const { data: logs } = usePoll(() => (projectId ? api.logs(projectId, { limit: 8 }) : Promise.resolve([])), 3000, [projectId]);

  if (!projectId) {
    return (
      <div className="p-8 fade-up">
        <div className="studio-card p-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#212534] flex items-center justify-center mb-4">
            <Heart size={30} className="text-[#E6B04E]" />
          </div>
          <h2 className="font-head text-2xl font-bold mb-1">Welcome to AI Wedding Studio</h2>
          <p className="text-[#94A3B8] text-sm mb-5 max-w-md">Create or open a wedding project to see its dashboard, import footage and run analysis.</p>
          <button className="btn btn-gold" onClick={() => navigate("/projects")}><FolderPlus size={16} /> Go to Projects</button>
        </div>
      </div>
    );
  }

  const m = data?.media;
  const jobs = data?.jobs;
  const qb = data?.quality_breakdown || {};
  const totalQ = Object.values(qb).reduce((a, b) => a + b, 0) || 1;
  const active = (jobs?.queued || 0) + (jobs?.running || 0);

  return (
    <div className="p-8 fade-up space-y-6">
      {/* header banner */}
      <div className="studio-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ background: "radial-gradient(circle at 80% 20%, #E6B04E, transparent 60%)" }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="metric-label mb-1">{project?.wedding_type} · {project?.editing_style}</div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">{project?.name}</h1>
            <p className="text-[#94A3B8] mt-1">{project?.bride_name} &amp; {project?.groom_name}
              {project?.venue ? ` · ${project.venue}` : ""}{project?.wedding_date ? ` · ${project.wedding_date}` : ""}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/media" className="btn btn-gold" data-testid="dash-import-btn"><Upload size={16} /> Import Media</Link>
            <Link to="/jobs" className="btn btn-ghost" data-testid="dash-jobs-btn"><Activity size={16} /> Job Queue</Link>
          </div>
        </div>
      </div>

      {/* metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={Film} label="Total Clips" value={m?.total ?? 0} sub={`${m?.ready ?? 0} ready · ${m?.remaining ?? 0} pending`} accent="#E6B04E" />
        <Metric icon={HardDrive} label="Raw Footage" value={fmtBytes(m?.raw_bytes || 0)} sub={`${fmtDuration(m?.total_duration || 0)} total runtime`} accent="#00F0FF" />
        <Metric icon={Sparkles} label="Analysis" value={`${data?.analysis_progress ?? 0}%`} sub={`${m?.scanned ?? 0}/${m?.total ?? 0} scanned`} accent="#8B5CF6" />
        <Metric icon={Copy} label="Duplicates" value={m?.duplicates ?? 0} sub={`${m?.failed ?? 0} failed jobs`} accent="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* quality breakdown */}
        <div className="studio-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold text-lg">Media Quality Breakdown</h3>
            <span className="metric-label">{m?.total ?? 0} clips</span>
          </div>
          {m?.total ? (
            <>
              <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-[#0c0e14]">
                {Object.entries(qb).map(([k, v]) => (
                  <div key={k} style={{ width: `${(v / totalQ) * 100}%`, background: QUALITY_COLORS[k] || "#64748B" }} />
                ))}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {["KEEP", "GOOD", "REVIEW", "POOR", "REJECTED", "UNRATED"].map((k) => (
                  <div key={k} className="text-center">
                    <div className="font-head text-xl font-bold" style={{ color: QUALITY_COLORS[k] }}>{qb[k] || 0}</div>
                    <div className="metric-label" style={{ fontSize: 9 }}>{k}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-[#64748B] text-sm py-8 text-center">No media imported yet. Head to Media to upload footage.</div>
          )}
        </div>

        {/* job status */}
        <div className="studio-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold text-lg">Background Queue</h3>
            {active > 0 && <span className="flex items-center gap-1.5 text-[11px] font-mono text-[#00F0FF]"><span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] pulse-dot" /> {active} active</span>}
          </div>
          <div className="space-y-2.5">
            {[
              ["running", "Running", "#00F0FF"], ["queued", "Queued", "#E6B04E"],
              ["completed", "Completed", "#10B981"], ["failed", "Failed", "#EF4444"],
              ["paused", "Paused", "#94A3B8"], ["cancelled", "Cancelled", "#64748B"],
            ].map(([k, label, c]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm text-[#94A3B8] flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{label}</span>
                <span className="font-mono text-sm font-semibold">{jobs?.[k] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* activity */}
      <div className="studio-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg">Recent Activity</h3>
          <Link to="/logs" className="text-[11px] font-mono text-[#E6B04E]">View all logs →</Link>
        </div>
        <div className="font-mono text-[12px] space-y-1.5">
          {(logs || []).slice().reverse().map((l) => (
            <div key={l.id} className="flex gap-3">
              <span className="text-[#475569]">{new Date(l.timestamp).toLocaleTimeString()}</span>
              <span className={l.level === "ERROR" ? "text-[#f87171]" : l.level === "WARN" ? "text-[#fbbf24]" : "text-[#94A3B8]"}>{l.message}</span>
            </div>
          ))}
          {(!logs || logs.length === 0) && <div className="text-[#64748B]">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
}
